import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Table from '../../components/Common/Table';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import Modal from '../../components/Common/Modal';
import Input from '../../components/Common/Input';
import { formatStatus } from '../../utils/formatters';
import {
  ArrowLeft,
  Edit,
  User,
  Phone,
  Mail,
  Briefcase,
  Users,
  Calendar,
  DollarSign,
  TrendingUp,
  Layout,
  ExternalLink,
  Shield,
  Clock,
  MapPin,
  Wallet
} from 'lucide-react';
import { usePermission } from '../../hooks/usePermission';
import { useAuth } from '../../contexts/AuthContext';

const UserDetail = () => {
  const { user } = useAuth();
  const auth_user_is_manager = user?.is_manager || user?.role === 'office_owner';

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { canEdit } = usePermission();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'customers');
  const [financeData, setFinanceData] = useState([]);
  const [financeFilters, setFinanceFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });

  // Ödeme Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await api.get(`/users/${id}`);
      setUserData(response.data);
    } catch (error) {
      console.error('Kullanıcı yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserFinance = async () => {
    try {
      const response = await api.get(`/finance/users/${id}`, { params: financeFilters });
      setFinanceData(response.data.data);
    } catch (error) {
      console.error('Finansal veriler yüklenemedi:', error);
    }
  };

  const handlePayment = async () => {
    if (!paymentAmount || isNaN(paymentAmount)) return;

    const remainingDebt = selectedCommission.office_commission.office_amount - (selectedCommission.office_commission.paid_amount || 0);
    if (parseFloat(paymentAmount) > remainingDebt) {
      alert(`Hata: Tahsilat tutarı kalan borçtan (${remainingDebt.toLocaleString('tr-TR')} ₺) fazla olamaz.`);
      return;
    }

    setPaymentLoading(true);
    try {
      await api.post('/finance/pay-office', {
        office_commission_id: selectedCommission.office_commission.id,
        amount: parseFloat(paymentAmount)
      });
      setShowPaymentModal(false);
      setPaymentAmount('');
      fetchUserFinance();
    } catch (error) {
      console.error('Ödeme hatası:', error);
      alert(error.response?.data?.message || 'Ödeme kaydedilirken bir hata oluştu.');
    } finally {
      setPaymentLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'finance') {
      fetchUserFinance();
    }
  }, [activeTab, financeFilters, id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <User size={40} className="text-red-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Personel bulunamadı</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Aradığınız personel sistemde kayıtlı olmayabilir.</p>
        <Button onClick={() => navigate('/departments/users')} className="mt-6">
          <ArrowLeft size={18} />
          Listeye Dön
        </Button>
      </div>
    );
  }

  const stats = [
    {
      label: 'Toplam Müşteri',
      value: userData.customers_count || 0,
      icon: Users,
      color: 'text-blue-600',
      bg: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-100 dark:border-blue-800/30'
    },
    {
      label: 'Görüşmeler',
      value: userData.meetings_count || 0,
      icon: Calendar,
      color: 'text-purple-600',
      bg: 'bg-purple-50 dark:bg-purple-900/20',
      borderColor: 'border-purple-100 dark:border-purple-800/30'
    },
    {
      label: 'Satışlar',
      value: userData.sales_count || 0,
      icon: TrendingUp,
      color: 'text-green-600',
      bg: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-100 dark:border-green-800/30'
    },
    {
      label: 'Kaporalar',
      value: userData.down_payments_count || 0,
      icon: DollarSign,
      color: 'text-yellow-600',
      bg: 'bg-yellow-50 dark:bg-yellow-900/20',
      borderColor: 'border-yellow-100 dark:border-yellow-800/30'
    },
  ];

  const customerColumns = [
    {
      header: 'Ad Soyad',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-600">
            {row.name.charAt(0)}{row.surname.charAt(0)}
          </div>
          <span className="font-medium text-gray-900 dark:text-white">{row.name} {row.surname}</span>
        </div>
      )
    },
    {
      header: 'Durum',
      render: (row) => {
        const getStatusColor = (status) => {
          switch (status) {
            case 'potential':
              return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            case 'qualified':
              return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'option':
              return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
            case 'definite_sale':
              return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
            default:
              return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
          }
        };

        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(row.status)}`}>
            {formatStatus(row.status)}
          </span>
        );
      }
    },
    {
      header: 'Bütçe',
      render: (row) => (
        <span className="font-semibold text-primary-600 dark:text-primary-400 text-sm">
          {row.budget ? `${row.budget.toLocaleString('tr-TR')} ₺` : '-'}
        </span>
      )
    },
    {
      header: 'İletişim',
      render: (row) => (
        <div className="flex items-center gap-3 text-gray-500">
          {row.email && <Mail size={14} title={row.email} />}
          {row.phone && <Phone size={14} title={row.phone} />}
        </div>
      )
    }
  ];

  const meetingColumns = [
    {
      header: 'Müşteri',
      render: (row) => (
        <div
          className="flex items-center gap-2 hover:text-primary-600 cursor-pointer transition-colors"
          onClick={() => navigate(`/customers/${row.customer_id}`)}
        >
          <span className="font-medium">{row.customer?.name} {row.customer?.surname}</span>
          <ExternalLink size={12} className="opacity-50" />
        </div>
      )
    },
    {
      header: 'Tarih',
      render: (row) => (
        <div className="flex items-center gap-2 text-sm">
          <Clock size={14} className="text-gray-400" />
          {new Date(row.date).toLocaleDateString('tr-TR')}
        </div>
      )
    },
    { header: 'Tip', render: (row) => formatStatus(row.type) },
    {
      header: 'Durum',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.is_completed ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
          }`}>
          {row.is_completed ? 'Tamamlandı' : 'Bekliyor'}
        </span>
      )
    },
  ];

  const salesColumns = [
    {
      header: 'Portföy',
      render: (row) => (
        <div
          className="flex items-center gap-2 hover:text-primary-600 cursor-pointer transition-colors max-w-xs"
          onClick={() => navigate(`/portfolios/${row.portfolio_id}`)}
        >
          <span className="font-medium truncate">{row.portfolio?.title}</span>
          <ExternalLink size={12} className="opacity-50" />
        </div>
      )
    },
    { header: 'Müşteri', render: (row) => `${row.customer?.name} ${row.customer?.surname}` },
    {
      header: 'Fiyat',
      render: (row) => (
        <span className="font-bold text-green-600 dark:text-green-400">
          {row.final_sale_price?.toLocaleString('tr-TR')} ₺
        </span>
      )
    },
    { header: 'Tarih', render: (row) => new Date(row.sale_date).toLocaleDateString('tr-TR') },
  ];

  const financeColumns = [
    {
      header: 'İşlem Bilgisi',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-bold text-gray-900 dark:text-white">
            {row.portfolio?.list_price?.toLocaleString('tr-TR')} ₺
          </span>
          <span className={`text-[10px] font-bold uppercase ${row.source_type === 'sale' ? 'text-blue-600' : 'text-indigo-600'}`}>
            {row.source_type === 'sale' ? 'Satış' : 'Kiralama'}
          </span>
        </div>
      )
    },
    {
      header: 'Müşteri Komisyonu',
      render: (row) => (
        <div className="flex flex-col">
          <span className={`text-sm font-bold ${row.status === 'cancelled' ? 'text-gray-400 line-through' : 'text-primary-600'}`}>
            {row.commission_amount?.toLocaleString('tr-TR')} ₺
          </span>
          <span className={`px-2 py-0.5 rounded text-[10px] w-fit font-medium mt-1 ${row.status === 'received' ? 'bg-green-100 text-green-700' :
            row.status === 'cancelled' ? 'bg-red-100 text-red-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
            {row.status === 'received' ? 'Alındı' : row.status === 'cancelled' ? 'İptal' : 'Bekliyor'}
          </span>
        </div>
      )
    },
    {
      header: 'Ofis Payı (Toplam)',
      render: (row) => (
        <span className={`font-bold ${row.status === 'cancelled' ? 'text-gray-400 line-through' : 'text-indigo-600'}`}>
          {row.office_commission?.office_amount?.toLocaleString('tr-TR')} ₺
        </span>
      )
    },
    {
      header: 'Alınan (Ofis)',
      render: (row) => (
        <span className="text-green-600 font-medium">
          {(row.office_commission?.paid_amount || 0).toLocaleString('tr-TR')} ₺
        </span>
      )
    },
    {
      header: 'Kalan (Ofis)',
      render: (row) => {
        const remaining = (row.office_commission?.office_amount || 0) - (row.office_commission?.paid_amount || 0);
        return (
          <span className={`font-bold ${remaining > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {remaining.toLocaleString('tr-TR')} ₺
          </span>
        );
      }
    },
    {
      header: 'Ofis Durum',
      render: (row) => (
        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${row.office_commission?.status === 'received' ? 'bg-green-100 text-green-700' :
          row.office_commission?.status === 'partially_received' ? 'bg-orange-100 text-orange-700' :
            row.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
          }`}>
          {row.office_commission?.status === 'received' ? 'Ödendi' :
            row.office_commission?.status === 'partially_received' ? 'Kısmen' :
              row.status === 'cancelled' ? 'İptal' : 'Bekliyor'}
        </span>
      )
    },
    {
      header: 'Ödeme',
      render: (row) => (
        <div className="flex justify-center">
          {row.office_commission?.status !== 'received' && row.status !== 'cancelled' && auth_user_is_manager && (
            <button
              onClick={() => {
                setSelectedCommission(row);
                setShowPaymentModal(true);
              }}
              className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
              title="Ödeme Tahsil Et"
            >
              <Wallet size={16} />
            </button>
          )}
        </div>
      )
    }
  ];

  const tabs = [
    { id: 'customers', label: 'Atanan Müşteriler', count: userData.customers?.length || 0, icon: Users },
    { id: 'meetings', label: 'Görüşme Geçmişi', count: userData.meetings_count || 0, icon: Calendar },
    { id: 'sales', label: 'Satış Kayıtları', count: userData.sales_count || 0, icon: TrendingUp },
    { id: 'finance', label: 'Finans', count: userData.commissions_count || 0, icon: DollarSign },
  ];

  return (
    <div className="space-y-6">
      {/* Top Navbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/departments/users')}
            className="p-2.5 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:bg-gray-50 transition-colors shadow-sm"
          >
            <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Personel Profili</h1>
            <p className="text-sm text-gray-500">Çalışan bilgilerini ve performansını inceleyin</p>
          </div>
        </div>
        {canEdit('users') && (
          <Button onClick={() => navigate(`/departments/users/${id}/edit`)} className="px-6 py-2.5 shadow-lg shadow-primary-500/20">
            <Edit size={18} />
            Bilgileri Düzenle
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar / Profile Card */}
        <div className="lg:col-span-1 space-y-6">
          <Card className="border-none shadow-xl shadow-gray-200/50 dark:shadow-none overflow-hidden">
            <div className="h-24 bg-gradient-to-r from-primary-600 to-indigo-600 -mx-6 -mt-6 mb-12 relative">
              <div className="absolute -bottom-10 left-6">
                <div className="w-20 h-20 rounded-2xl bg-white dark:bg-gray-800 p-1 shadow-lg">
                  <div className="w-full h-full rounded-xl bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
                    <User size={36} className="text-primary-600 dark:text-primary-400" />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6 pt-2">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{userData.name}</h2>
                  {userData.is_manager && (
                    <Shield size={16} className="text-purple-500" title="Yönetici" />
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Briefcase size={14} />
                  <span>{userData.department?.name || 'Departman Atanmamış'}</span>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-3 group">
                  <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 transition-colors">
                    <Mail size={18} className="text-gray-400 dark:text-gray-500 group-hover:text-primary-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">E-Posta</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{userData.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 group">
                  <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 transition-colors">
                    <Phone size={18} className="text-gray-400 dark:text-gray-500 group-hover:text-primary-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">Telefon</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{userData.phone || '-'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 group">
                  <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 transition-colors">
                    <MapPin size={18} className="text-gray-400 dark:text-gray-500 group-hover:text-primary-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">Ofis</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{userData.office?.name || '-'}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 group">
                  <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 transition-colors">
                    <Shield size={18} className="text-gray-400 dark:text-gray-500 group-hover:text-primary-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">Departmanı</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {userData.is_manager ? 'Yönetici' : formatStatus(userData.department.name)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <div className={`flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-bold ${userData.status === 'active'
                  ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 border border-green-100 dark:border-green-800/30'
                  : 'bg-gray-50 text-gray-500 dark:bg-gray-800 dark:text-gray-400 border border-gray-100 dark:border-gray-700'
                  }`}>
                  <div className={`w-2 h-2 rounded-full ${userData.status === 'active' ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                  {userData.status === 'active' ? 'AKTİF KULLANICI' : 'PASİF KULLANICI'}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map((stat, i) => (
              <div
                key={i}
                className={`bg-white dark:bg-gray-900 p-5 rounded-3xl border ${stat.borderColor} shadow-sm group hover:shadow-xl transition-all duration-500`}
              >
                <div className="flex flex-col gap-3">
                  <div className={`${stat.bg} w-12 h-12 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                    <stat.icon className={`${stat.color}`} size={22} />
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold uppercase tracking-[0.1em]">{stat.label}</p>
                    <p className="text-2xl font-black text-gray-900 dark:text-white mr-1">{stat.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* User Records Tabs */}
          <div className="bg-white dark:bg-gray-900 rounded-[2rem] border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="flex border-b border-gray-100 dark:border-gray-800 overflow-x-auto bg-gray-50/50 dark:bg-gray-800/50">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-3 px-8 py-5 text-sm font-bold transition-all relative whitespace-nowrap ${activeTab === tab.id
                    ? 'text-primary-600 bg-white dark:bg-gray-900'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                >
                  <tab.icon size={18} className={activeTab === tab.id ? 'text-primary-600' : 'text-gray-400'} />
                  {tab.label}
                  <span className={`px-2 py-0.5 rounded-lg text-[10px] ${activeTab === tab.id ? 'bg-primary-100 text-primary-700' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                    {tab.count}
                  </span>
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary-600 rounded-t-full shadow-[0_-4px_10px_rgba(37,99,235,0.3)]" />
                  )}
                </button>
              ))}
            </div>

            <div className="p-0">
              {activeTab === 'customers' && (
                <div className="animate-in slide-in-from-bottom-2 duration-300">
                  <Table
                    columns={customerColumns}
                    data={userData.customers || []}
                    onView={(row) => navigate(`/customers/${row.id}`)}
                  />
                  {(!userData.customers || userData.customers.length === 0) && (
                    <div className="py-20 text-center">
                      <Users size={48} className="mx-auto text-gray-200 dark:text-gray-800 mb-4" />
                      <p className="text-gray-400 font-medium">Henüz atanan müşteri bulunmuyor</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'meetings' && (
                <div className="animate-in slide-in-from-bottom-2 duration-300">
                  <Table
                    columns={meetingColumns}
                    data={userData.meetings || []}
                    onView={(row) => navigate(`/meetings/${row.id}`)}
                  />
                  {(!userData.meetings || userData.meetings.length === 0) && (
                    <div className="py-20 text-center">
                      <Calendar size={48} className="mx-auto text-gray-200 dark:text-gray-800 mb-4" />
                      <p className="text-gray-400 font-medium">Henüz görüşme kaydı bulunmuyor</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'sales' && (
                <div className="animate-in slide-in-from-bottom-2 duration-300">
                  <Table
                    columns={salesColumns}
                    data={userData.sales || []}
                    onView={(row) => navigate(`/portfolios/${row.portfolio_id}`)}
                  />
                  {(!userData.sales || userData.sales.length === 0) && (
                    <div className="py-20 text-center">
                      <TrendingUp size={48} className="mx-auto text-gray-200 dark:text-gray-800 mb-4" />
                      <p className="text-gray-400 font-medium">Henüz satış kaydı bulunmuyor</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'finance' && (
                <div className="animate-in slide-in-from-bottom-2 duration-300">
                  <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex justify-end gap-2 bg-gray-50/30">
                    <select
                      className="text-xs border rounded-lg p-1 dark:bg-gray-800"
                      value={financeFilters.month}
                      onChange={e => setFinanceFilters({ ...financeFilters, month: e.target.value })}
                    >
                      {[...Array(12)].map((_, i) => (
                        <option key={i + 1} value={i + 1}>{new Date(2022, i).toLocaleString('tr-TR', { month: 'long' })}</option>
                      ))}
                    </select>
                    <select
                      className="text-xs border rounded-lg p-1 dark:bg-gray-800"
                      value={financeFilters.year}
                      onChange={e => setFinanceFilters({ ...financeFilters, year: e.target.value })}
                    >
                      {[0, 1, 2].map(i => (
                        <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>
                      ))}
                    </select>
                  </div>
                  <Table
                    columns={financeColumns}
                    data={financeData || []}
                  />
                  {(!financeData || financeData.length === 0) && (
                    <div className="py-20 text-center">
                      <Wallet size={48} className="mx-auto text-gray-200 dark:text-gray-800 mb-4" />
                      <p className="text-gray-400 font-medium">Bu dönemde finansal kayıt bulunmuyor</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Ofis Ödemesi Tahsil Et"
      >
        {selectedCommission && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-center">
              <div>
                <p className="text-[10px] text-gray-500 uppercase">Toplam Borç</p>
                <p className="font-bold text-gray-900 dark:text-white">{selectedCommission.office_commission.office_amount.toLocaleString('tr-TR')} ₺</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500 uppercase">Ödenen</p>
                <p className="font-bold text-green-600">{(selectedCommission.office_commission.paid_amount || 0).toLocaleString('tr-TR')} ₺</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500 uppercase">Kalan</p>
                <p className="font-bold text-red-600">{(selectedCommission.office_commission.office_amount - (selectedCommission.office_commission.paid_amount || 0)).toLocaleString('tr-TR')} ₺</p>
              </div>
            </div>

            <Input
              label="Tahsil Edilen Tutar"
              type="number"
              placeholder="Örn: 500"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              error={paymentAmount > (selectedCommission.office_commission.office_amount - (selectedCommission.office_commission.paid_amount || 0)) ? 'Tahsil edilecek tutar kalan borçtan fazla olamaz.' : null}
            />

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>İptal</Button>
              <Button
                onClick={handlePayment}
                loading={paymentLoading}
                disabled={!paymentAmount || paymentAmount <= 0}
              >
                Ödemeyi Kaydet
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserDetail;
